import { Grid, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Formik } from 'formik';
import { FormikUtils } from '../../shared/utils';
import { CerebroForm } from '../../shared/components/CerebroForm';
import { CerebroInput } from '../../shared/components/CerebroInput';
import { CerebroPrimaryButton } from '../../shared/components/CerebroPrimaryButton';
import { useApi, useAuth } from '../../shared/context';
import { useSnackbar } from 'notistack';
import { useMutation } from 'react-query';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import {
	Deal,
	LenderMatchDrillIn,
	ShowSendPreMarketDealEmails,
	StructureTrancheMap,
	StructureTranches,
	Tier1BankTypeMap,
	Tier1BankTypes,
	Tier3ContactTypeMap,
	Tier3ContactTypes,
} from '../../shared/models';
import React from 'react';
import { CerebroDropdown, CerebroDropdownMenuItem } from '../../shared/components/CerebroDropdown';
import { GetStructureTrancheValue, GetTier1BankTypeValue, GetTier3ContactTypeValue } from '../../shared/utils/enum';
import { CerebroSecondaryButton } from '../../shared/components/CerebroSecondaryButton';
import { CerebroLabel } from '../../shared/components/CerebroLabel';
import { getApiResponseData } from '../../shared/utils/axios';
import { AxiosError } from 'axios';
import { LenderMatchRowClickResponse, LenderMatchTableRow } from './LenderMatchTableRow';

interface MatchingSearchProps {
	dealGuid?: string;
	deal?: Deal;
}

export const MatchingSearch: React.FC<MatchingSearchProps> = ({ dealGuid, deal }) => {
	const { currentUser } = useAuth();
	const api = useApi();
	const snackbar = useSnackbar();

	// search fields
	const [keywords, setKeywords] = React.useState<string>('');
	const [searchTier1, setSearchTier1] = React.useState<string>('Any Tier 1 Type');
	const [searchTier3, setSearchTier3] = React.useState<string>('Any Tier 3 Type');
	const [searchMode, setSearchMode] = React.useState<number>(1);
	const [geographyMode, setGeographyMode] = React.useState<number>(1);
	const showPreMarketDealButton = ShowSendPreMarketDealEmails(currentUser, deal?.dealStatus);

	// teaser form fields
	const [structureType, setStructureType] = React.useState<string>('SeniorCashFlow');
	const [addToDataRoom, setAddToDataRoom] = React.useState<boolean>(false);
	const [sendPreMarketDealAlert, setSendPreMarketDealAlert] = React.useState<boolean>(false);

	// display fields
	const [isSearching, setIsSearching] = React.useState<boolean>(false);
	const [results, setResults] = React.useState<LenderMatchDrillIn[]>([] as LenderMatchDrillIn[]);

	const resetSearchTab = () => {
		setKeywords('');
		setSearchMode(1);
		setSearchTier1('Any Tier 1 Type');
		setSearchTier3('Any Tier 3 Type');
		setStructureType('SeniorCashFlow');
		setResults([] as LenderMatchDrillIn[]);
		setIsSearching(false);
		setAddToDataRoom(false);
		setGeographyMode(1);
	};

	const searchLenders = () => {
		setIsSearching(true);
		setResults([] as LenderMatchDrillIn[]);
		searchMutation.mutate({
			values: {
				search: keywords,
				tier1: searchTier1,
				tier3: searchTier3,
				mode: searchMode,
				geographyMode: geographyMode,
			},
		} as LendersSearchMutationVariables);
	};

	const searchMutation = useMutation(
		async ({ values }: LendersSearchMutationVariables) => {
			const { data, error } = await getApiResponseData<LenderMatchDrillIn[]>(
				api.get(
					CerebroApiEndpoints.Matching.Search(
						dealGuid,
						values.search,
						GetTier1BankTypeValue(values.tier1 as Tier1BankTypes),
						GetTier3ContactTypeValue(values.tier3 as Tier3ContactTypes),
						values.mode,
						values.geographyMode
					)
				)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			if (data == null) {
				throw new Error('No lenders found matching the search criteria.');
			}

			return data;
		},
		{
			onSuccess: data => {
				setIsSearching(false);
				setResults(data);
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
				setIsSearching(false);
			},
		}
	);

	const executeLenderMutationAction = async (
		dealGuid: string | undefined,
		trancheSelected: number | null,
		values: LendersSearchValues
	): Promise<{ data: string[] | null; error: AxiosError | null }> => {
		if (addToDataRoom) {
			const { data, error } = await getApiResponseData<string[]>(
				api.post(
					CerebroApiEndpoints.ParticipantList.Root(dealGuid, trancheSelected),
					values.bankDealProfileGuid
				)
			);

			return { data, error };
		}

		if (sendPreMarketDealAlert) {
			const { data, error } = await getApiResponseData<string[]>(
				api.post(CerebroApiEndpoints.PreMarketEmail.Root(dealGuid), values.bankDealProfileGuid)
			);

			return { data, error };
		}

		const { data, error } = await getApiResponseData<string[]>(
			api.post(CerebroApiEndpoints.LenderList.Root(dealGuid, trancheSelected), values.bankDealProfileGuid)
		);

		return { data, error };
	};

	const getSuccessMessage = (): string => {
		if (addToDataRoom) {
			return 'Successfully added selected lenders to the data room participant list';
		}

		if (sendPreMarketDealAlert) {
			return 'Successfully sent pre-market deal alert to selected lenders';
		}

		return 'Successfully added selected lenders to the teaser list';
	};

	const lendersMutation = useMutation(
		async ({ values }: LendersSearchValuesMutationVariables) => {
			const { data, error } = await executeLenderMutationAction(
				dealGuid,
				GetStructureTrancheValue(structureType as StructureTranches),
				values
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: (_data, { helpers }) => {
				snackbar.enqueueSnackbar(getSuccessMessage(), {
					variant: 'success',
				});
				helpers.resetForm({
					values: {
						bankDealProfileGuid: [] as string[],
					},
				});
				resetSearchTab();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<>
			<Grid container>
				<Grid item xs={12} lg={2}>
					<CerebroInput
						value={keywords}
						disabled={isSearching}
						onChange={data => setKeywords(data.target.value)}
					/>
				</Grid>
				<Grid item xs={12} lg={2}>
					<CerebroDropdown
						value={searchTier1}
						disabled={isSearching}
						onChange={data => setSearchTier1(data.target.value as string)}
					>
						<CerebroDropdownMenuItem value="Any Tier 1 Type">Any Tier 1 Type</CerebroDropdownMenuItem>
						{Object.entries(Tier1BankTypeMap).map(([value, description]) => (
							<CerebroDropdownMenuItem key={value} value={value}>
								{description}
							</CerebroDropdownMenuItem>
						))}
					</CerebroDropdown>
				</Grid>
				<Grid item xs={12} lg={2}>
					<CerebroDropdown
						value={searchTier3}
						disabled={isSearching}
						onChange={data => setSearchTier3(data.target.value as string)}
					>
						<CerebroDropdownMenuItem value="Any Tier 3 Type">Any Tier 3 Type</CerebroDropdownMenuItem>
						{Object.entries(Tier3ContactTypeMap).map(([value, description]) => (
							<CerebroDropdownMenuItem key={value} value={value}>
								{description}
							</CerebroDropdownMenuItem>
						))}
					</CerebroDropdown>
				</Grid>
				<Grid item xs={12} sm={2}>
					<CerebroDropdown
						value={geographyMode}
						onChange={data => setGeographyMode(data.target.value as number)}
					>
						<CerebroDropdownMenuItem value="1">Match Geography</CerebroDropdownMenuItem>
						<CerebroDropdownMenuItem value="2">Ignore Geography</CerebroDropdownMenuItem>
					</CerebroDropdown>
				</Grid>
				<Grid item xs={12} lg={2}>
					<CerebroDropdown
						value={searchMode}
						disabled={isSearching}
						onChange={data => setSearchMode(data.target.value as number)}
					>
						<CerebroDropdownMenuItem value="1">Include All</CerebroDropdownMenuItem>
						<CerebroDropdownMenuItem value="2">Exclude Active</CerebroDropdownMenuItem>
						<CerebroDropdownMenuItem value="3">Exclude All Invited</CerebroDropdownMenuItem>
						<CerebroDropdownMenuItem value="4">Specialty Industry</CerebroDropdownMenuItem>
						<CerebroDropdownMenuItem value="5">Loan Watch Subscribers</CerebroDropdownMenuItem>
						<CerebroDropdownMenuItem value="6">New Lenders</CerebroDropdownMenuItem>
					</CerebroDropdown>
				</Grid>
				<Grid item xs={12} lg={2}>
					<CerebroPrimaryButton
						sx={{ marginLeft: 1, marginTop: 1.5 }}
						disabled={
							isSearching ||
							(!keywords &&
								searchTier1 == 'Any Tier 1 Type' &&
								searchTier3 == 'Any Tier 3 Type' &&
								searchMode == 1 &&
								geographyMode == 1)
						}
						onClick={() => searchLenders()}
					>
						Search
					</CerebroPrimaryButton>
				</Grid>
			</Grid>

			<Formik
				onSubmit={FormikUtils.MutateOnSubmit(lendersMutation.mutate)}
				initialValues={{ bankDealProfileGuid: [] as string[] } as LendersSearchValues}
			>
				{({ isSubmitting, handleSubmit, values, setFieldValue }) => (
					<CerebroForm flatIndicator onSubmit={handleSubmit}>
						<TableContainer>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell>Select</TableCell>
										<TableCell>Lender Name</TableCell>
										<TableCell>Bank Name</TableCell>
										<TableCell>Tier 3</TableCell>
										<TableCell>Criteria</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{isSearching && (
										<>
											<TableRow>
												<TableCell colSpan={5}>
													<Skeleton />
												</TableCell>
											</TableRow>
											<TableRow>
												<TableCell colSpan={5}>
													<Skeleton />
												</TableCell>
											</TableRow>
											<TableRow>
												<TableCell colSpan={5}>
													<Skeleton />
												</TableCell>
											</TableRow>
										</>
									)}

									{!isSearching &&
										results.length > 0 &&
										results.map(lender => {
											return (
												<LenderMatchTableRow
													key={lender.bankDealProfileGuid}
													lender={lender}
													isSubmitting={isSearching}
													showCapacityColumn={false}
													allowSelection={true}
													isChecked={
														values.bankDealProfileGuid.find(
															w => w == lender.bankDealProfileGuid
														) != null
													}
													onChange={(item: LenderMatchRowClickResponse) => {
														if (item.isChecked) {
															values.bankDealProfileGuid.push(item.bankDealProfileGuid);
															setFieldValue(
																'bankDealProfileGuid',
																values.bankDealProfileGuid
															);
														} else {
															const id = values.bankDealProfileGuid.indexOf(
																item.bankDealProfileGuid
															);
															if (id > -1) {
																values.bankDealProfileGuid.splice(id, 1);
																setFieldValue(
																	'bankDealProfileGuid',
																	values.bankDealProfileGuid
																);
															}
														}
													}}
												/>
											);
										})}
									{results.length == 0 && (
										<TableRow>
											<TableCell colSpan={5} sx={{ textAlign: 'center' }}>
												No results, please select search criteria.
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>

						<Grid container sx={{ marginTop: 3 }}>
							<Grid item xs={12} lg={6}>
								<CerebroLabel>Which tranche will the lenders be linked to?</CerebroLabel>

								<CerebroDropdown
									value={structureType}
									disabled={isSearching || !Object.values(values).some(checked => checked)}
									onChange={data => setStructureType(data.target.value as string)}
								>
									{Object.entries(StructureTrancheMap).map(([value, description]) => (
										<CerebroDropdownMenuItem key={value} value={value}>
											{description}
										</CerebroDropdownMenuItem>
									))}
								</CerebroDropdown>
							</Grid>
						</Grid>

						<CerebroPrimaryButton
							type="submit"
							loading={isSubmitting}
							disabled={!values.bankDealProfileGuid || values.bankDealProfileGuid.length == 0}
							sx={{ marginTop: 6, marginRight: 1 }}
							onClick={() => setAddToDataRoom(false)}
						>
							Add to Teaser List
						</CerebroPrimaryButton>

						<CerebroSecondaryButton
							type="submit"
							loading={isSubmitting}
							disabled={!values.bankDealProfileGuid || values.bankDealProfileGuid.length == 0}
							sx={{ marginTop: 6 }}
							onClick={() => setAddToDataRoom(true)}
						>
							Add to Data Room
						</CerebroSecondaryButton>

						{showPreMarketDealButton && (
							<CerebroSecondaryButton
								type="submit"
								loading={isSubmitting}
								disabled={!values.bankDealProfileGuid || values.bankDealProfileGuid.length == 0}
								sx={{ marginTop: 6, marginLeft: 1 }}
								onClick={() => setSendPreMarketDealAlert(true)}
							>
								Send Pre-Market Deal Alert
							</CerebroSecondaryButton>
						)}
					</CerebroForm>
				)}
			</Formik>
		</>
	);
};

type LendersSearchMutationVariables = FormikUtils.MutateOnSubmitVariables<LendersSearch>;
interface LendersSearch {
	search: string;
	tier1: string;
	tier3: string;
	mode: number;
	geographyMode: number;
}

type LendersSearchValuesMutationVariables = FormikUtils.MutateOnSubmitVariables<LendersSearchValues>;
interface LendersSearchValues {
	bankDealProfileGuid: string[];
}
